import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    Menu,
    MenuItem,
    Slider,
    Divider,
    ListItemIcon,
    Chip,
    CssBaseline,
    ThemeProvider,
    createTheme,
} from '@mui/material';
import {
    AccountCircle,
    AddAPhoto,
    DeleteOutline,
    Logout,
    Dashboard as DashboardIcon,
    Settings,
    AdminPanelSettings,
    Home,
    NoteAlt,
    AccountBalance,
    Notifications as NotificationsIcon,
    PieChart,
    CloudDownload,
    CloudUpload,
} from '@mui/icons-material';
import { useMsal } from '@azure/msal-react';
import { clearTokenCache } from '../auth/apiClient';
import { FRONTEND_ORIGIN } from '../auth/authConfig';
import { getProfilePhoto, uploadProfilePhoto, removeProfilePhoto } from '../services/profileService';

const AppLayout = () => {
    const { instance } = useMsal();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
    const [profilePhotoLoading, setProfilePhotoLoading] = useState(false);
    const [profilePhotoUploading, setProfilePhotoUploading] = useState(false);
    const [showPhotoDialog, setShowPhotoDialog] = useState(false);
    const [pendingPhotoDataUrl, setPendingPhotoDataUrl] = useState('');
    const [cropZoom, setCropZoom] = useState(1);
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const saved = localStorage.getItem('theme');
        return saved ? saved === 'dark' : true;   // dark by default
    });

    useEffect(() => {
        document.body.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
        localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    const muiTheme = useMemo(() => {
        const dark = isDarkMode;
        return createTheme({
            palette: {
                mode: dark ? 'dark' : 'light',
                primary: { main: dark ? '#2A8FFF' : '#2563EB' },
                secondary: { main: dark ? '#20D3B0' : '#0D9B7A' },
                error: { main: '#EF5350' },
                warning: { main: '#FFA726' },
                success: { main: '#66BB6A' },
                background: {
                    default: dark ? '#070B12' : '#F4F6F8',
                    paper: dark ? '#0F1A2B' : '#F8FAFC',
                },
                text: {
                    primary: dark ? '#E6EDF7' : '#0F172A',
                    secondary: dark ? '#A9B7D0' : '#475569',
                },
            },
            typography: {
                fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
                button: { textTransform: 'none', fontWeight: 600 },
            },
            components: {
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundImage: 'none',
                            backgroundColor: 'var(--bg-2)',
                            color: 'var(--text-1)',
                        },
                    },
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            textTransform: 'none',
                            borderRadius: 8,
                            fontWeight: 600,
                        },
                    },
                },
                MuiCard: {
                    styleOverrides: {
                        root: {
                            borderRadius: 12,
                            border: '1px solid var(--border)',
                            boxShadow: 'var(--shadow-sm)',
                            backgroundColor: 'var(--card-bg)',
                        },
                    },
                },
                MuiTableCell: {
                    styleOverrides: {
                        root: {
                            borderBottom: '1px solid var(--border)',
                            color: 'var(--text-1)',
                        },
                        head: {
                            backgroundColor: 'var(--table-head-bg)',
                            color: 'var(--table-head-text)',
                            fontWeight: 700,
                            fontSize: '0.8125rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        },
                    },
                },
                MuiTextField: {
                    styleOverrides: {
                        root: {
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'var(--input-bg)',
                                color: 'var(--input-text)',
                                '& fieldset': {
                                    borderColor: 'var(--input-border)',
                                },
                            },
                        },
                    },
                },
            },
        });
    }, [isDarkMode]);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleMenuClose();
        // Clear token cache before logout
        clearTokenCache();
        instance.logoutRedirect({
            postLogoutRedirectUri: FRONTEND_ORIGIN,
        }).catch((error) => {
            console.error('[AppLayout] Logout failed:', error);
        });
    };

    const handleDashboard = () => {
        handleMenuClose();
        navigate('/dashboard');
    };

    const handleAdminOperations = () => {
        navigate('/admin/operations');
    };

    const handleDataScrape = () => {
        navigate('/admin/data-scrape');
    };

    const handleDocumentIngest = () => {
        navigate('/admin/ingest');
    };

    const handleResearchNotes = () => {
        handleMenuClose();
        navigate('/notes');
    };

    const handleFunds = () => {
        handleMenuClose();
        navigate('/funds');
    };

    const handlePortfolio = () => {
        handleMenuClose();
        navigate('/portfolio');
    };

    const toggleTheme = () => {
        setIsDarkMode(prev => !prev);
    };

    const activeAccount = useMemo(() => instance.getActiveAccount(), [instance]);
    const uploadInputRef = useRef(null);
    const cropPreviewRef = useRef(null);
    const displayName = activeAccount?.name || activeAccount?.username || 'User';
    const displayEmail = activeAccount?.username || '';

    useEffect(() => {
        let cancelled = false;

        const loadProfilePhoto = async () => {
            setProfilePhotoLoading(true);
            try {
                const raw = await getProfilePhoto();
                const data = raw && typeof raw === 'object' && 'data' in raw ? raw.data : raw;
                if (!cancelled) {
                    setProfilePhotoUrl(data?.hasPhoto ? (data.photoDataUrl || '') : '');
                }
            } catch (error) {
                if (!cancelled) {
                    setProfilePhotoUrl('');
                }
            } finally {
                if (!cancelled) {
                    setProfilePhotoLoading(false);
                }
            }
        };

        if (activeAccount) {
            loadProfilePhoto();
        } else {
            setProfilePhotoLoading(false);
        }

        return () => { cancelled = true; };
    }, [activeAccount]);

    const getUserInitials = () => {
        if (displayName) {
            const names = displayName.split(' ');
            if (names.length >= 2) {
                return `${names[0][0]}${names[1][0]}`.toUpperCase();
            }
            return displayName[0]?.toUpperCase() || 'U';
        }
        return 'U';
    };

    const getDisplayName = () => displayName;
    const getProfilePhotoUrl = () => profilePhotoUrl;
    const getProfilePhotoLoading = () => profilePhotoLoading;

    const handleUploadPhotoClick = () => {
        uploadInputRef.current?.click();
    };

    const uploadDataUrl = async (dataUrl) => {
        try {
            setProfilePhotoUploading(true);
            const raw = await uploadProfilePhoto(dataUrl);
            const data = raw && typeof raw === 'object' && 'data' in raw ? raw.data : raw;
            setProfilePhotoUrl(data?.hasPhoto ? (data.photoDataUrl || '') : '');
            setShowPhotoDialog(false);
            setPendingPhotoDataUrl('');
            setCropZoom(1);
            handleMenuClose();
        } catch (error) {
            alert('Failed to upload profile photo. Please try again.');
        } finally {
            setProfilePhotoUploading(false);
        }
    };

    const cropCenterSquareDataUrl = () => {
        const image = cropPreviewRef.current;
        if (!image || !image.naturalWidth || !image.naturalHeight) {
            return pendingPhotoDataUrl;
        }

        const minSide = Math.min(image.naturalWidth, image.naturalHeight);
        const cropSide = minSide / Math.max(1, cropZoom);
        const sx = (image.naturalWidth - cropSide) / 2;
        const sy = (image.naturalHeight - cropSide) / 2;

        const canvas = document.createElement('canvas');
        canvas.width = 320;
        canvas.height = 320;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, sx, sy, cropSide, cropSide, 0, 0, canvas.width, canvas.height);
        return canvas.toDataURL('image/jpeg', 0.92);
    };

    const handleConfirmCropUpload = async () => {
        const cropped = cropCenterSquareDataUrl();
        await uploadDataUrl(cropped);
    };

    const handleUploadOriginal = async () => {
        await uploadDataUrl(pendingPhotoDataUrl);
    };

    const handleRemovePhoto = async () => {
        try {
            setProfilePhotoUploading(true);
            await removeProfilePhoto();
            setProfilePhotoUrl('');
            handleMenuClose();
        } catch (error) {
            alert('Failed to remove profile photo. Please try again.');
        } finally {
            setProfilePhotoUploading(false);
        }
    };

    const handlePhotoSelected = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Please select an image file (JPG, PNG, WEBP).');
            event.target.value = '';
            return;
        }

        if (file.size > (2 * 1024 * 1024)) {
            alert('Image too large. Maximum allowed size is 2MB.');
            event.target.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = async () => {
            setPendingPhotoDataUrl(String(reader.result || ''));
            setCropZoom(1);
            setShowPhotoDialog(true);
            event.target.value = '';
        };
        reader.onerror = () => {
            alert('Unable to read selected file.');
            event.target.value = '';
        };
        reader.readAsDataURL(file);
    };

    return (
        <ThemeProvider theme={muiTheme}>
            <CssBaseline />
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                {/* Main Content - No padding, let pages control their own layout */}
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        bgcolor: 'var(--bg-0)',
                        minHeight: '100vh',
                    }}
                >
                    <Outlet context={{
                        isDarkMode,
                        toggleTheme,
                        handleAdminOperations,
                        getUserInitials,
                        getDisplayName,
                        getProfilePhotoUrl,
                        getProfilePhotoLoading,
                        displayEmail,
                        handleMenuOpen,
                        handleMenuClose,
                        anchorEl,
                        navigate
                    }} />
                </Box>

                {/* User Dropdown Menu - Shared across all pages */}
                <Menu
                    id="user-menu"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    PaperProps={{
                        sx: {
                            mt: 1.5,
                            minWidth: 220,
                            bgcolor: 'var(--bg-2)',
                            color: 'var(--text-1)',
                            border: '1px solid var(--border)',
                            boxShadow: 'var(--shadow)',
                            borderRadius: 2,
                        },
                    }}
                >
                    {/* User Info */}
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            {displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {displayEmail}
                        </Typography>
                        <Chip
                            label="Admin"
                            size="small"
                            color="primary"
                            sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                        />
                    </Box>

                    <Divider />

                    {/* Menu Items */}
                    <MenuItem onClick={() => { handleMenuClose(); navigate('/home'); }}>
                        <ListItemIcon>
                            <Home fontSize="small" />
                        </ListItemIcon>
                        Home
                    </MenuItem>

                    <MenuItem onClick={handleDashboard}>
                        <ListItemIcon>
                            <DashboardIcon fontSize="small" />
                        </ListItemIcon>
                        Stocks Research
                    </MenuItem>

                    <MenuItem onClick={handleFunds}>
                        <ListItemIcon>
                            <AccountBalance fontSize="small" />
                        </ListItemIcon>
                        Mutual Funds / Indices
                    </MenuItem>

                    <MenuItem onClick={handlePortfolio}>
                        <ListItemIcon>
                            <PieChart fontSize="small" />
                        </ListItemIcon>
                        Live Portfolio
                    </MenuItem>

                    <MenuItem onClick={handleResearchNotes}>
                        <ListItemIcon>
                            <NoteAlt fontSize="small" />
                        </ListItemIcon>
                        Research Notes
                    </MenuItem>

                    <MenuItem onClick={() => { handleMenuClose(); handleAdminOperations(); }}>
                        <ListItemIcon>
                            <AdminPanelSettings fontSize="small" />
                        </ListItemIcon>
                        Admin Operations
                    </MenuItem>

                    <MenuItem onClick={() => { handleMenuClose(); handleDocumentIngest(); }}>
                        <ListItemIcon>
                            <CloudUpload fontSize="small" />
                        </ListItemIcon>
                        Ingest Annual Report
                    </MenuItem>

                    <MenuItem onClick={() => { handleMenuClose(); handleDataScrape(); }}>
                        <ListItemIcon>
                            <CloudDownload fontSize="small" />
                        </ListItemIcon>
                        Data Scrape
                    </MenuItem>

                    <Divider />

                    <MenuItem onClick={() => { handleMenuClose(); navigate('/funds?tab=global-markets'); }}>
                        <ListItemIcon>
                            <NotificationsIcon fontSize="small" />
                        </ListItemIcon>
                        Market Notifications
                    </MenuItem>

                    <MenuItem onClick={handleUploadPhotoClick} disabled={profilePhotoUploading}>
                        <ListItemIcon>
                            <AddAPhoto fontSize="small" />
                        </ListItemIcon>
                        {profilePhotoUploading ? 'Uploading photo...' : 'Upload Profile Photo'}
                    </MenuItem>

                    <MenuItem onClick={handleRemovePhoto} disabled={profilePhotoUploading || !profilePhotoUrl}>
                        <ListItemIcon>
                            <DeleteOutline fontSize="small" />
                        </ListItemIcon>
                        Remove Profile Photo
                    </MenuItem>

                    <MenuItem onClick={handleMenuClose}>
                        <ListItemIcon>
                            <AccountCircle fontSize="small" />
                        </ListItemIcon>
                        Profile
                    </MenuItem>

                    <MenuItem onClick={handleMenuClose}>
                        <ListItemIcon>
                            <Settings fontSize="small" />
                        </ListItemIcon>
                        Settings
                    </MenuItem>

                    <Divider />

                    <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                            <Logout fontSize="small" color="error" />
                        </ListItemIcon>
                        <Typography color="error">Logout</Typography>
                    </MenuItem>
                </Menu>

                <input
                    ref={uploadInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    style={{ display: 'none' }}
                    onChange={handlePhotoSelected}
                />

                <Dialog
                    open={showPhotoDialog}
                    onClose={() => !profilePhotoUploading && setShowPhotoDialog(false)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Preview & Crop Profile Photo</DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 1 }}>
                            <Box
                                sx={{
                                    width: 220,
                                    height: 220,
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    margin: '0 auto',
                                    border: '1px solid var(--border)',
                                    backgroundColor: 'var(--bg-2)',
                                    position: 'relative',
                                }}
                            >
                                {pendingPhotoDataUrl && (
                                    <img
                                        ref={cropPreviewRef}
                                        src={pendingPhotoDataUrl}
                                        alt="Profile preview"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transform: `scale(${cropZoom})`,
                                        }}
                                    />
                                )}
                            </Box>

                            <Typography variant="caption" sx={{ display: 'block', mt: 2, mb: 0.5 }}>
                                Zoom for center crop
                            </Typography>
                            <Slider
                                min={1}
                                max={2.5}
                                step={0.05}
                                value={cropZoom}
                                onChange={(_, value) => setCropZoom(Number(value))}
                                disabled={profilePhotoUploading}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setShowPhotoDialog(false)}
                            disabled={profilePhotoUploading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUploadOriginal}
                            disabled={profilePhotoUploading || !pendingPhotoDataUrl}
                        >
                            Upload Original
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirmCropUpload}
                            disabled={profilePhotoUploading || !pendingPhotoDataUrl}
                        >
                            {profilePhotoUploading ? 'Uploading...' : 'Upload Cropped'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </ThemeProvider>
    );
};

export default AppLayout;
